<template>
  <v-container grid-list-md fluid>
    <template v-if="!loadingTax">
      <v-layout>
        <v-flex xs4>
          <v-card>
            <v-card-title class="headline">Taxonomy: {{tax.name}}</v-card-title>
            <v-list v-if="tax.tax">
              <v-list-tile v-for="rank in taxRanks" :key="rank">
                <v-list-tile-content>
                  <v-list-tile-title>{{rank}}</v-list-tile-title>
                  <v-list-tile-sub-title>
                    <template v-if="tax.tax[rank]">
                      <router-link :to="{name:'view-taxonomy', params:{'id': tax.tax[`${rank}_id`]}}" v-if="tax.tax[`${rank}_id`] != tax.taxonomy_id">
                        {{tax.tax[rank]}}
                      </router-link>
                      <span v-else>
                        {{tax.tax[rank]}}
                      </span>
                    </template>
                    <span v-else>
                      N/A
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-card>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs12>
            <v-toolbar color="transparent" class="elevation-0">
              <v-toolbar-title>Lineage</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="showAddDescendant = true" :disabled="disableDescendantAdd">
                Add {{nextRankLevel}}
              </v-btn>
            </v-toolbar>
            <v-data-table
              v-if="!disableDescendantList"
              :headers="descendantHeaders"
              :items="descendants"
              :loading="descendantLoading"
              hide-actions
              class="elevation-1">
              <template v-slot:items="props">
                <td style="width: 80px;">
                  <router-link :to="{name:'view-taxonomy', params:{id: props.item.taxonomy_id}}">{{props.item.taxonomy_id}}</router-link>
                </td>
                <td style="width: 80px;" class="text-xs-left">
                  <a target="_" :href="`https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${props.item.ncbi_id}`">{{ props.item.ncbi_id}}</a>
                </td>
                <td class="text-xs-left"  v-for="rankName in taxRanks" :key="rankName">
                  {{props.item.tax[rankName]}}
                </td>
              </template>
            </v-data-table>
            <p v-else>
              Lineage table has been disabled at the kingdom level due to amount of data at this high of rank level
            </p>
        </v-flex>
        <v-dialog v-model="showAddDescendant" width="500px">
          <v-card>
            <v-card-title class="headline">
              Add new {{nextRankLevel}} descendant
            </v-card-title>
            <v-card-text>
              This new taxonomy will be a descendant under  {{tax.rank}}: {{tax.name}}
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text>
              <p class="red--text" v-show="newDescendant.error">{{newDescendant.error}}</p>
              <v-text-field
                :label="`New ${nextRankLevel} name`"
                v-model="newDescendant.name"
              ></v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="secondary" @click="showAddDescendant = false">
                Cancel
              </v-btn>
              <v-btn @click="submitDescendant" color="primary" :disabled="!newDescendant.name" :loading="submittingDescendant">
                Submit
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>
    </template>
  </v-container>
</template>

<script>
import TaxonomyAPI from '@/api/taxonomy.js'
export default {
  props: ['id'],
  data () {
    return {
      newDescendant: {
        name: undefined,
        error: undefined
      },
      validAddLevel: ['genus', 'species'],
      taxRanks: ['kingdom', 'phylum', 'class', 'order', 'family', 'genus', 'species', 'strain'],
      nextRankLevel: null,
      submittingDescendant: false,
      tax: null,
      loadingTax: false,
      descendantLoading: false,
      descendants: [],
      error: '',
      showAddDescendant: false
    }
  },
  created () {
    this.load()
  },
  computed: {
    disableDescendantAdd () {
      return !this.nextRankLevel || this.validAddLevel.indexOf(this.tax.rank) === -1
    },
    disableDescendantList () {
      return this.tax.rank === 'kingdom'
    },
    descendantHeaders () {
      const taxHeaders = this.taxRanks.map((n) => ({'text': n, 'value': `tax.${n}`}))
      return [
        {
          text: 'Taxonomy Id',
          value: 'taxonomy_id'
        },
        {
          text: 'NCBI Id',
          value: 'ncbi_id'
        },
        ...taxHeaders
      ]
    }
  },
  methods: {
    addToLineage (newDescendant) {
      /**
       * fake it here and grab the tax tree from our parent as we dont
       * get this info from the return response
       */
      const newTax = {
        ...newDescendant,
        tax: Object.assign({[newDescendant.rank]: newDescendant.name}, this.tax.tax)
      }
      this.descendants.unshift(newTax)
    },
    async submitDescendant () {
      this.submittingDescendant = true
      this.newDescendant.error = undefined
      try {
        const {data} = await TaxonomyAPI.create({
          name: this.newDescendant.name,
          rank: this.nextRankLevel,
          parentTaxonomyId: this.tax.taxonomy_id
        })
        this.addToLineage(data)
        this.showAddDescendant = false
      } catch (error) {
        const {message} = error.response.data
        this.newDescendant.error = message
      } finally {
        this.submittingDescendant = false
      }
    },
    async load () {
      await this.getTax()
      if (!this.disableDescendantList) {
        await this.getLineage()
      }
    },
    async getLineage () {
      this.descendantLoading = true
      const {data} = await TaxonomyAPI.getLineage(this.id)
      this.descendants = data
      this.descendantLoading = false
    },
    async getTax () {
      this.loadingTax = true
      const filterParams = {
        taxonomy_id: {
          filter: 'equals',
          value: this.id
        }
      }
      try {
        const {data} = await TaxonomyAPI.getById(this.id, {filterParams})
        if (data) {
          this.tax = data
          this.setNextTaxLevel()
        } else {
          this.error = `Taxonomy ID ${this.id} not found`
        }
      } catch (error) {
        this.error = `Error loading Taxonomy ID ${this.id}`
      } finally {
        this.loadingTax = false
      }
    },
    setNextTaxLevel () {
      /**
       * This is primary to find next in order what would be the next level
       * to add new descendants. Example if this is a genus, the next rank in the
       * list is species. So the user can only add a new species.
       */
      const rankIdx = this.taxRanks.indexOf(this.tax.rank)
      if (rankIdx !== -1 && rankIdx !== this.taxRanks.length - 1) {
        this.nextRankLevel = this.taxRanks[rankIdx + 1]
      } else {
        this.nextRankLevel = undefined
      }
    }
  },
  watch: {
    '$route' (to, from) {
      this.load()
    }
  }
}
</script>

<style>

</style>
